/**
 * Site KPI drill-down wiring: the scope every drill carries, the chart click
 * router, the OTIF / Spend card period drills (CR-6284), and the two
 * specialised modals' state.
 *
 * CR-5868: extracted from SiteKPITab.tsx (tab split into panels to stay under
 * the 500-line limit).
 */
import { useState } from 'react'
import { useDrilldownAction } from '../../drilldown'
import type { DashboardData, DashboardFilterState, RechartsClickData } from '../types'
import { parseMonthToDateRange, type KPIPeriod } from '../SiteKPIComponents'
import { buildOtifDrillFilters, otifMonthBucketFilters, type OtifDrillOptions } from '../dashboardFilterUtils'
import { LIVE_NCRS_OVERDUE_METRIC, liveNcrsDrillFilters, type NcrLifecycle } from '../liveNcrsDrill'

/**
 * What a Site KPI chart click drills into. `liveNcrs` is the LIVE NCRs OVERDUE
 * chart (its own `ncr_live_overdue` drill, CR-6676); `ncrs` is every other NCR
 * chart on the panel (the shared `ncr` drill).
 */
export type SiteKPIMetricType = 'ppm' | 'otif' | 'otd' | 'spend' | 'arrears' | 'ncrs' | 'liveNcrs' | 'appeals'
export type OTIFModalTab = 'failed' | 'arrears' | 'positive' | 'total'
export type OTDModalTab = 'on_time' | 'early' | 'late' | 'total'

interface ModalState<Tab> {
  isOpen: boolean
  context?: string
  initialTab?: Tab
  filters: Record<string, string>
}

/**
 * Drill-down state and handlers for the Site KPI tab.
 *
 * @param data - the dashboard payload (for the Live NCRs Overdue lookup, CR-6676)
 * @param filters - the page-level dashboard filters the drills must mirror
 * @param isSupplier - CR-5868: on the supplier portal the panel's Site is a customer site
 * @param orderType - CR-7751: the dashboard's order-type toggle, as the tile reads it
 * @returns the drill scope, `openDrilldown`, the chart click router and modal state
 */
export function useSiteKPIDrilldowns(
  data: DashboardData,
  filters: DashboardFilterState | undefined,
  isSupplier = false,
  orderType?: OtifDrillOptions['orderType'],
) {
  // Hook for programmatic drill-down (for chart clicks)
  const { openDrilldown } = useDrilldownAction()

  const [otifModal, setOtifModal] = useState<ModalState<OTIFModalTab>>({ isOpen: false, filters: {} })
  const [otdModal, setOtdModal] = useState<ModalState<OTDModalTab>>({ isOpen: false, filters: {} })

  // CR-6405: the shared OTIF drill scope. This tab used to hand-build its own
  // copy, which is how Strategic Suppliers Only and the call-off basis went
  // missing here while reaching the Default tab.
  const drilldownFilters: Record<string, string> = buildOtifDrillFilters(filters, { isSupplier, orderType })

  // Handle chart click - open drill-down modal with month-specific data
  const handleChartClick = (chartData: RechartsClickData | null, metricType: SiteKPIMetricType, segment?: NcrLifecycle) => {
    if (!chartData) return
    let month: string | null = null
    if (chartData.activePayload && chartData.activePayload[0]) {
      month = (chartData.activePayload[0].payload?.['month'] as string) || (chartData.activePayload[0].payload?.['site'] as string) || null
    } else if (chartData.activeLabel) {
      month = chartData.activeLabel
    }
    if (!month) return

    // Parse month to date range for API filtering
    const dateRange = parseMonthToDateRange(month)
    const chartFilters = dateRange ? {
      ...drilldownFilters,
      date_from: dateRange.date_from,
      date_to: dateRange.date_to,
    } : drilldownFilters

    // For OTIF, use the specialized OTIF drill-down modal with month-specific filters
    if (metricType === 'otif') {
      let initialTab: OTIFModalTab | undefined = undefined
      if (chartData.activePayload && chartData.activePayload.length > 0) {
        for (const item of chartData.activePayload) {
          if (item.dataKey === 'failed') { initialTab = 'failed'; break }
          else if (item.dataKey === 'arrears') { initialTab = 'arrears'; break }
          else if (item.dataKey === 'otifCount') { initialTab = 'positive'; break }
        }
      }
      // CR-6405: the month-bucket rule (window + grain) is declared once, in
      // otifMonthBucketFilters. This tab's month bar never sent `grain` while
      // the Default tab's identical bar did — the same drift, one level down.
      setOtifModal({ isOpen: true, context: month, initialTab, filters: otifMonthBucketFilters(drilldownFilters, dateRange) })
      return
    }

    // For OTD (depth of delivery), use the specialized OTD drill-down modal with month-specific filters
    if (metricType === 'otd') {
      let initialTab: OTDModalTab | undefined = undefined
      if (chartData.activePayload && chartData.activePayload.length > 0) {
        for (const item of chartData.activePayload) {
          if (item.dataKey === 'onTime') { initialTab = 'on_time'; break }
          else if (item.dataKey === 'early') { initialTab = 'early'; break }
          else if (item.dataKey === 'late') { initialTab = 'late'; break }
        }
      }
      setOtdModal({ isOpen: true, context: month, initialTab, filters: chartFilters })
      return
    }

    // Map metric types to drilldown keys
    const metricKeyMap: Record<string, string> = {
      'ppm': 'ppm',
      'spend': 'spend',
      'arrears': 'arrears',
      'ncrs': 'ncr',
      'liveNcrs': LIVE_NCRS_OVERDUE_METRIC,
      'appeals': 'appeals',
    }

    const metricKey = metricKeyMap[metricType]
    if (metricKey) {
      // CR-6676: scope the NCR drill-down to the clicked "Live NCRs Overdue"
      // segment (overdue by default; pending for a month with nothing overdue).
      // The Live NCRs Overdue chart opens its own metric, which lists exactly
      // the bar's NCRs (ncr_date window, same overdue rule) — liveNcrsDrill.ts.
      if (metricType === 'ncrs' || metricType === 'liveNcrs') {
        openDrilldown(metricKey, liveNcrsDrillFilters(chartFilters, data.charts?.liveNCRsOverdue, month, segment), month)
        return
      }
      openDrilldown(metricKey, chartFilters, month)
    }
  }

  // CR-6284: a card period (MONTH / 3 MONTH / 12 MONTH) drills into the same
  // trailing months of the chart series the card figure is summed over, kept
  // inside the dashboard's own window, so the list matches the tile.
  const periodDrill = (series: Array<{ month: string }> | undefined, months: KPIPeriod) => {
    const recent = (series || []).slice(-months)
    const first = recent.length > 0 ? parseMonthToDateRange(recent[0].month) : null
    const last = recent.length > 0 ? parseMonthToDateRange(recent[recent.length - 1].month) : null
    if (!first || !last) return { filters: drilldownFilters, context: undefined }
    const from = drilldownFilters['date_from']
    const to = drilldownFilters['date_to']
    return {
      filters: {
        ...drilldownFilters,
        date_from: from && from > first.date_from ? from : first.date_from,
        date_to: to && to < last.date_to ? to : last.date_to,
      },
      context: recent.length === 1 ? recent[0].month : `${recent[0].month} – ${recent[recent.length - 1].month}`,
    }
  }

  const openOtifPeriod = (months: KPIPeriod) => {
    const { filters: periodFilters, context } = periodDrill(data.charts?.otifData, months)
    // The card is the pooled OTIF % over every line, so it opens on the Total
    // tab — as the Default tab's OTIF card does.
    setOtifModal({ isOpen: true, context, initialTab: 'total', filters: periodFilters })
  }

  const openSpendPeriod = (months: KPIPeriod) => {
    const { filters: periodFilters, context } = periodDrill(data.charts?.spendByMonth, months)
    openDrilldown('spend', periodFilters, context)
  }

  return {
    drilldownFilters,
    openDrilldown,
    handleChartClick,
    openOtifPeriod,
    openSpendPeriod,
    otifModal,
    closeOtifModal: () => setOtifModal((m) => ({ ...m, isOpen: false })),
    otdModal,
    closeOtdModal: () => setOtdModal((m) => ({ ...m, isOpen: false })),
  }
}

export type SiteKPIDrilldowns = ReturnType<typeof useSiteKPIDrilldowns>
